import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  RedeemUxProvider,
  type RedeemUxValue,
  type MmMoneyIssuerProfile,
} from '@vaiu/redeem-ux';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { i18n } from '@/utils/i18n';
import { platformApi } from '@/services/platformApi';
import type { RedeemInfo } from '@/types/api/token';
import { currencyService } from '@/services/currencyService';
import {
  emitTokenRedemptionBalanceUpdate,
  emitBalanceUpdate,
} from '@/utils/lightEventEmitter';
import TopUpBalance from '@/components/TopUpBalance';
import StripeProvider from '@/components/payment/StripeProvider';
import InvoicePaymentReceived from '@/components/shared/InvoicePaymentReceived';
import { normalizeMmMoneyBrandingFromCurrencyApiPayload } from '@vaiu/utils/mmMoney';

async function fetchMmMoneyIssuerProfileForRedeem(
  issuerUserId: string
): Promise<MmMoneyIssuerProfile | null> {
  const id = issuerUserId.trim();
  if (!id) return null;

  try {
    const { merchants } = await platformApi.getPublicMerchantProfilesBatch([id]);
    const m = merchants[0];
    if (m) {
      const biz = (m.businessName || '').trim();
      const contact = (m.contactName || '').trim();
      return {
        issuerUserId: id,
        displayName: biz || undefined,
        name: contact && contact !== biz ? contact : undefined,
        username: (m as { username?: string }).username,
        currencyIconUrl: m.mmMoneyBranding?.currencyIconUrl || m.logo,
      };
    }
  } catch {
    /* try user profile */
  }

  try {
    const userProfile = await platformApi.user.getPublicUserProfile(id);
    const merchantRole = userProfile.public?.otherProfiles?.find((p) => p.role === 'MERCHANT');
    const mp = merchantRole?.profile as Record<string, unknown> | undefined;
    const biz =
      (typeof mp?.businessName === 'string' && mp.businessName.trim()) ||
      (typeof mp?.name === 'string' && mp.name.trim()) ||
      '';
    const person = [userProfile.username].filter(Boolean).join(' ').trim();
    return {
      issuerUserId: id,
      displayName: biz || undefined,
      name: person || undefined,
      username: userProfile.username,
    };
  } catch {
    /* optional branding from currency metadata */
  }

  try {
    const raw = await fetch('/api/v1/currency/details', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(localStorage.getItem('authToken')
          ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
          : {}),
      },
      body: JSON.stringify({ currencyId: `MM$${id}` }),
    });
    if (raw.ok) {
      const json = await raw.json();
      const branding = normalizeMmMoneyBrandingFromCurrencyApiPayload(json);
      if (branding.currencyIconUrl) {
        return { issuerUserId: id, currencyIconUrl: branding.currencyIconUrl };
      }
    }
  } catch {
    /* ignore */
  }

  return { issuerUserId: id };
}

export function VaiuRedeemUxProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();

  const value = useMemo<RedeemUxValue>(
    () => ({
      platformApi: {
        revealToken: (tokenKey: string, redeemAmount?: number) => {
          const u = user?.username?.trim() || user?._id?.trim();
          if (!u) {
            return Promise.reject(new Error('Cannot reveal token: missing username'));
          }
          return platformApi.revealToken(tokenKey, u, redeemAmount);
        },
        redeemToken: (
          tokenKey: string,
          redeemAmount?: number,
          transactor?: 'balance' | 'identity'
        ) => platformApi.redeemToken(tokenKey, redeemAmount, transactor),
        payInvoiceToken: (tokenKey: string, redeemInfo: unknown) =>
          platformApi.payInvoiceToken(tokenKey, redeemInfo as RedeemInfo),
        payInvoice: (paymentData) => platformApi.payInvoice(paymentData),
        getInvoiceDetails: (invoiceCode: string) => platformApi.getInvoiceDetails(invoiceCode),
        getMerchantProfile: () => platformApi.getMerchantProfile(),
        getPreferences: (preferenceId: string) =>
          platformApi.getPreferences(preferenceId as Parameters<typeof platformApi.getPreferences>[0]),
      },
      getUser: () => user,
      toast: (opts) => toast(opts),
      i18n,
      emitTokenRedemptionBalanceUpdate,
      emitBalanceUpdate,
      formatCurrency: (amount, currency) => currencyService.formatCurrency(amount, currency),
      getCurrencyDetails: (code) => currencyService.getCurrencyDetails(code),
      fetchMmMoneyIssuerProfile: fetchMmMoneyIssuerProfileForRedeem,
      Link,
      profileAutoDepositTo: '/profile',
      InvoicePaymentReceived,
      TopUpBalance,
      StripeProvider,
    }),
    [user, toast]
  );

  return <RedeemUxProvider value={value}>{children}</RedeemUxProvider>;
}
